import React, { useEffect, useState } from "react";
import Grid from "utils/Grid";
import { Button, Typography, Card } from "@mui/material";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "dayjs";

import styles from "./styles";
import MethodSection from "../MethodSection";
import ValueSection from "../ValueSection";
import MinimumPurchaseSection from "../MinimumPurchaseSection";
import ActiveDateSection from "../ActiveDateSection";
import SelectMachinesSection from "../SelectMachinesSection";
import SummarySection from "../SummarySection";
import { DiscountsProService } from "../../../../../../services/Api";
import { handleServerErrors } from "../../../../../../helpers";
import { BigLoader } from "components/Common";
import StickyHeader from "../StickyHeader";
import { cardBackgroundColor } from "../../utiles/helpers";

const AddDiscountByProductApp = (props) => {

  const classes = styles();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedDiscountMethod, setSelectedDiscountMethod] = useState("DISCOUNT_CODE");
  const [discountCode, setDiscountCode] = useState("");
  const [isCodeVisible, setIsCodeVisible] = useState(true);
  const [discountDescription, setDiscountDescription] = useState("");
  const [discountImage, setDiscountImage] = useState(null);
  const [discountImageData, setDiscountImageData] = useState(null);
  const [selectedValueType, setSelectedValueType] = useState("PERCENTAGE");
  const [selectedValueTypeInput, setSelectedValueTypeInput] = useState("");
  const [selectedAppliesToList, setSelectedAppliesToList] = useState([]);
  const [selectedAppliesTo, setSelectedAppliesTo] = useState("SPECIFIC_PRODUCT_CATEGORIES");
  const [minimumPurchase, setMinimumPurchase] = useState("NO_MINIMUM_REQUIREMENTS");
  const [minimumPurchaseInput, setMinimumPurchaseInput] = useState("");
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(null);
  const [selectedMachinesList, setSelectedMachinesList] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    const orderDetails = location?.state?.discountDetails;
    if (orderDetails) {
      setEditData(orderDetails);
      setTitle("Edit product discount");
    } else {
      setTitle("Create product discount");
    }
  }, []);

  const setEditData = (orderDetails) => {
    setSelectedDiscountMethod(orderDetails.method);
    setDiscountCode(orderDetails.discount_code);
    setIsCodeVisible(orderDetails.is_visible);
    setDiscountDescription(orderDetails.description);
    setDiscountImage(orderDetails.discount_image);
    setSelectedValueType(orderDetails.discount_value_type);
    setSelectedValueTypeInput(orderDetails.discount_value);

    setMinimumPurchase(orderDetails.min_purchase_requirement);
    if (orderDetails.min_purchase_requirement === "MINIMUM_PURCHASE_AMOUNT") {
      setMinimumPurchaseInput(orderDetails.min_purchase_amount);
    } else if (orderDetails.min_purchase_requirement === "MINIMUM_QUANTITY_OF_ITEMS") {
      setMinimumPurchaseInput(orderDetails.min_purchase_qty);
    }

    setSelectedAppliesTo(orderDetails.applies_to_buy);
    if (orderDetails.applies_to_buy === "SPECIFIC_PRODUCT_CATEGORIES") {
      const formated = orderDetails.buy_categories.map(({ id, category_name }) => {
        return {
          label: `${category_name}`,
          value: id,
        };
      });
      setSelectedAppliesToList(formated);
    } else if (orderDetails.applies_to_buy === "SPECIFIC_PRODUCT_SUBCATEGORIES") {
      const formated = orderDetails.buy_subcategories.map(({ id, category_name }) => {
        return {
          label: `${category_name}`,
          value: id,
        };
      });
      setSelectedAppliesToList(formated);
    } else if (orderDetails.applies_to_buy === "SPECIFIC_BRANDS") {
      const formated = orderDetails.buy_brands.map(({ id, brand }) => {
        return {
          label: `${brand}`,
          value: id,
        };
      });
      setSelectedAppliesToList(formated);
    } else if (orderDetails.applies_to_buy === "SPECIFIC_PRODUCTS") {
      const formated = orderDetails.buy_products.map(({ id, product_name }) => {
        return {
          label: `${product_name}`,
          value: id,
        };
      });
      setSelectedAppliesToList(formated);
    }

    setStartDate(orderDetails.start_date);
    setEndDate(orderDetails.end_date);

    const selectedMachines = orderDetails.assign_to.map(({ id, serial_number, vm_name }) => {
      return {
        label: `${vm_name ? `${vm_name} ||` : ""} ${serial_number}`,
        value: id,
      };
    });
    setSelectedMachinesList(selectedMachines);
  }

  const minimumPurchaseChanged = (value) => {
    setMinimumPurchase(value);
  }

  const minimumPurchaseInputChange = (value) => {
    setMinimumPurchaseInput(value);
  }

  const handleDiscountTypeChange = (type) => {
    setSelectedDiscountMethod(type);
  }

  const handleMethodSectionValueChanges = (key, value) => {
    if (key === "code") {
      setDiscountCode(value);
    } else if (key === "description") {
      setDiscountDescription(value);
    }
  }

  const onMachineSelected = (selectedMachines) => {
    setSelectedMachinesList(selectedMachines);
  }

  const onStartDateChange = (selectedStartDate) => {
    setStartDate(selectedStartDate);
  }

  const onEndDateChange = (selectedEndDate) => {
    setEndDate(selectedEndDate);
  }

  const onValueTypeChange = (value) => {
    setSelectedValueType(value)
  }

  const onValueTypeInputChange = (value) => {
    setSelectedValueTypeInput(value);
  }

  const onAppliesToSelected = (list, appliesTo) => {
    setSelectedAppliesToList(list);
    if (appliesTo) {
      setSelectedAppliesTo(appliesTo);
    }
  }

  const onDiscountImageSelected = (url, data) => {
    setDiscountImageData(data);
    setDiscountImage(url);
  }

  const isCodeVisibleCheckboxChange = (event) => {
    setIsCodeVisible(event.target.checked);
  }

  const onSavePressed = () => {

    if (discountCode.length <= 0) {
      enqueueSnackbar("Please enter discount code.");
      return;
    }
    if (!startDate) {
      enqueueSnackbar("Please select start date.");
      return;
    }
    if (moment(startDate) > moment(endDate)) {
      enqueueSnackbar("Start date and time must be earlier than end date and time.");
      return;
    }

    if (moment(startDate).diff(moment(endDate), 'days') === 0 && moment(startDate) >= moment(endDate)) {
      enqueueSnackbar("Start date and time must be earlier than end date and time.")
      return;
    }

    if (minimumPurchase === "MINIMUM_PURCHASE_AMOUNT"
      && (minimumPurchaseInput.length <= 0 || minimumPurchaseInput <= 0)) {
      enqueueSnackbar("Please enter minimum purchase amount greater than 0.");
      return;
    }
    
    if (minimumPurchase === "MINIMUM_QUANTITY_OF_ITEMS"
      && (minimumPurchaseInput.length <= 0 || minimumPurchaseInput > 5 || minimumPurchaseInput < 1)) {
      enqueueSnackbar("Please enter minimum quantity of items between 1 to 5.");
      return;
    }
    
    if (selectedValueType === "PERCENTAGE"
      && (selectedValueTypeInput.length <= 0 || selectedValueTypeInput > 100 || selectedValueTypeInput <= 0)) {
      enqueueSnackbar("Please enter discount percentage between 1 to 100");
      return;
    }
    if (selectedValueType === "FIXED_AMOUNT" && (selectedValueTypeInput.length <= 0 || selectedValueTypeInput <= 0)) {
      enqueueSnackbar("Please enter discount amount greater than 0.");
      return;
    }

    if (selectedValueType === "FIXED_AMOUNT" && minimumPurchase === "MINIMUM_PURCHASE_AMOUNT"
      && Number(minimumPurchaseInput) < Number(selectedValueTypeInput)) {
      enqueueSnackbar("Minimum purchase amount cannot be less than the Fixed discount amount.");
      return;
    }

    if (selectedAppliesToList && selectedAppliesToList.length <= 0) {
      if (selectedAppliesTo === "SPECIFIC_PRODUCT_CATEGORIES") {
        enqueueSnackbar("Please select a product category in Applies To section");
      } else if (selectedAppliesTo === "SPECIFIC_PRODUCT_SUBCATEGORIES") {
        enqueueSnackbar("Please select a product subcategory in Applies To section");
      } else if (selectedAppliesTo === "SPECIFIC_BRANDS") {
        enqueueSnackbar("Please select a brand in Applies To section");
      } else if (selectedAppliesTo === "SPECIFIC_PRODUCTS") {
        enqueueSnackbar("Please select a product in Applies To section");
      }
      return;
    }

    setShowLoader(true);
    const fd = new FormData();
    fd.append("deals_on", "AMOUNT_OFF_PRODUCTS");
    fd.append("method", selectedDiscountMethod);
    fd.append("discount_code", discountCode);
    fd.append("is_visible", isCodeVisible);
    fd.append("description", discountDescription);
    if (discountImageData) {
      fd.append("discount_image", discountImageData);
    }
    fd.append("discount_value_type", selectedValueType);
    fd.append("discount_value", selectedValueTypeInput);
    fd.append("applies_to_buy", selectedAppliesTo);
    fd.append("min_purchase_requirement", minimumPurchase);
    fd.append("start_date", startDate ? moment(startDate).format() : null);
    fd.append("end_date", endDate ? moment(endDate).format() : null);

    if (minimumPurchase === "MINIMUM_PURCHASE_AMOUNT") {
      fd.append("min_purchase_amount", minimumPurchaseInput);
      fd.append("min_purchase_qty", 0);
    } else if (minimumPurchase === "MINIMUM_QUANTITY_OF_ITEMS") {
      fd.append("min_purchase_qty", minimumPurchaseInput);
      fd.append("min_purchase_amount", 0);
    } else if (minimumPurchase === "NO_MINIMUM_REQUIREMENTS") {
      fd.append("min_purchase_qty", 0);
      fd.append("min_purchase_amount", 0);
    }

    if (selectedMachinesList) {
      const machineIDs = JSON.stringify(selectedMachinesList.map(item => item.value)) || "[]";
      fd.append("assign_to", machineIDs);
    }

    if (selectedAppliesToList && selectedAppliesToList.length > 0) {
      const appliesToIDs = JSON.stringify(selectedAppliesToList.map(item => item.value));
      fd.append("buy_categories", JSON.stringify([]));
      fd.append("buy_subcategories", JSON.stringify([]));
      fd.append("buy_brands", JSON.stringify([]));
      fd.append("buy_products", JSON.stringify([]));

      if (selectedAppliesTo === "SPECIFIC_PRODUCT_CATEGORIES") {
        fd.append("buy_categories", appliesToIDs);
      } else if (selectedAppliesTo === "SPECIFIC_PRODUCT_SUBCATEGORIES") {
        fd.append("buy_subcategories", appliesToIDs);
      } else if (selectedAppliesTo === "SPECIFIC_BRANDS") {
        fd.append("buy_brands", appliesToIDs);
      } else if (selectedAppliesTo === "SPECIFIC_PRODUCTS") {
        fd.append("buy_products", appliesToIDs);
      }
    }

    if (location?.state?.discountDetails) {
      DiscountsProService.editDiscount(location.state.discountDetails.id, fd)
      .then(() => {
        enqueueSnackbar("Discount edited successfully.");
        navigate(-1);
      })
      .catch((err) => {
        if (err.response.data.detail) {
          enqueueSnackbar(err.response.data.detail);
        } else {
          handleServerErrors(
            err,
            enqueueSnackbar,
            "Could not edit discount. Try again."
          );
        }
      }).finally(() => {
        setShowLoader(false);
      });
    } else {
      fd.append("operator", "");
      DiscountsProService.addDiscount(fd)
      .then(() => {
        enqueueSnackbar("Discount created successfully.");
        navigate(-1);
      })
      .catch((err) => {
        if (err.response.data.detail) {
          enqueueSnackbar(err.response.data.detail);
        } else {
          handleServerErrors(
            err,
            enqueueSnackbar,
            "Could not add discount. Try again."
          );
        }
      }).finally(() => {
        setShowLoader(false);
      });
    }
  }

  const onBackPressed = () => {
    navigate(-1);
  }

  return (
    <div>
      <div className={classes.header}>
        <StickyHeader
          headerTitle={title}
          onSavePressed={onSavePressed}
          onBackPressed={onBackPressed}
        />
      </div>
      <Grid container className={classes.container}>
        <Grid sm={12} style={{ padding: 10, background: cardBackgroundColor, borderRadius: 5 }}>
          <MethodSection
            discountType="AMOUNT_OFF_PRODUCTS"
            discountMethod={selectedDiscountMethod}
            discountCode={discountCode}
            isCodeVisible={isCodeVisible}
            discountDescription={discountDescription}
            discountImage={discountImage}
            onDiscountImageSelected={onDiscountImageSelected}
            handleDiscountTypeChange={handleDiscountTypeChange}
            handleMethodSectionValueChanges={handleMethodSectionValueChanges}
            isCodeVisibleCheckboxChange={isCodeVisibleCheckboxChange}
          />
          <ValueSection
            discountType="AMOUNT_OFF_PRODUCTS"
            isAutomatic={selectedDiscountMethod === "AUTOMATIC_DISCOUNT" ? true : false}
            selectedValueType={selectedValueType}
            selectedValueTypeInput={selectedValueTypeInput}
            selectedAppliesTo={selectedAppliesTo}
            selectedAppliesToValues={selectedAppliesToList}
            onAppliesToSelected={onAppliesToSelected}
            onValueTypeChange={onValueTypeChange}
            onValueTypeInputChange={onValueTypeInputChange}
          />
          <MinimumPurchaseSection
            purchaseType={minimumPurchase}
            minimumPurchaseInput={minimumPurchaseInput}
            minimumPurchaseChanged={minimumPurchaseChanged}
            minimumPurchaseInputChange={minimumPurchaseInputChange}
          />
          <ActiveDateSection
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
          />
          <SelectMachinesSection
            selectedMachinesList={selectedMachinesList}
            onMachineSelected={onMachineSelected}
          />
        </Grid>
        {/* <Grid sm={4}>
          <SummarySection 
            discountType="AMOUNT_OFF_PRODUCTS"
            discountMethod={selectedDiscountMethod}
            discountActiveStatus={location?.state?.discountDetails?.active_status}
          />
        </Grid> */}
      </Grid>
      {showLoader && <BigLoader/>}
    </div>
  );
}

export default AddDiscountByProductApp;